import React, { useState, useEffect, useRef } from 'react';
import MapFrame from '../../components/MapFrame';
import BlockSubtitle from '../../components/BlockSubtitle';
import MusicPlayer from '../../components/MusicPlayer';
import './Aquarelle.css';

/* ── Helpers ─────────────────────────────────────────────────── */
const src = u => !u ? null : u.startsWith('http') ? u : `${u}`;
const fmtDate = raw => {
  if (!raw) return 'ДД · ММ · ГГГГ';
  const d = new Date(raw);
  return [d.getDate(), d.getMonth() + 1, d.getFullYear()]
    .map(n => String(n).padStart(2, '0')).join(' · ');
};

const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;

/* ── useReveal ───────────────────────────────────────────────── */
function useReveal(pageRef, blockKey) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const SEL = '.aq-reveal, .aq-reveal-left, .aq-reveal-up, .aq-bloom';
    const inPreview = !!root.closest('.lp-template-inner, .lp-template-outer');
    if (inPreview) {
      root.querySelectorAll(SEL).forEach(el => el.classList.add('aq-in'));
      return;
    }
    const els = root.querySelectorAll(SEL);
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('aq-in'); obs.unobserve(e.target); }
      });
    }, { threshold: 0.1 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, [pageRef, blockKey]);
}

/* ── Countdown ───────────────────────────────────────────────── */
function Countdown({ date, accent }) {
  const [t, setT] = useState({ d: 0, h: 0, m: 0, s: 0 });
  useEffect(() => {
    const tick = () => {
      const ms = Math.max(0, new Date(date) - Date.now());
      setT({ d: Math.floor(ms / 86400000), h: Math.floor(ms % 86400000 / 3600000),
             m: Math.floor(ms % 3600000 / 60000), s: Math.floor(ms % 60000 / 1000) });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [date]);
  return (
    <div className="aq-countdown">
      {[['дней', t.d], ['часов', t.h], ['минут', t.m], ['секунд', t.s]].map(([u, v]) => (
        <div key={u} className="aq-cu">
          <span className="aq-cu-val" style={{ color: accent }}>{String(v).padStart(2, '0')}</span>
          <span className="aq-cu-label">{u}</span>
        </div>
      ))}
    </div>
  );
}

/* ── CalendarWidget ──────────────────────────────────────────── */
function CalendarWidget({ date, accent, addBtn }) {
  if (!date) return null;
  const d = new Date(date), yr = d.getFullYear(), mo = d.getMonth(), day = d.getDate();
  const off = (new Date(yr, mo, 1).getDay() + 6) % 7;
  const dim = new Date(yr, mo + 1, 0).getDate();
  const prevDim = new Date(yr, mo, 0).getDate();
  const cells = [];
  for (let i = off - 1; i >= 0; i--) cells.push({ n: prevDim - i, cur: false });
  for (let i = 1; i <= dim; i++) cells.push({ n: i, cur: true });
  while (cells.length < 42) cells.push({ n: cells.length - off - dim + 1, cur: false });
  const MON = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
  return (
    <div className="aq-calendar">
      <div className="aq-cal-header">
        <span style={{ color: accent, fontWeight: 700 }}>{MON[mo]}</span>
        <span className="aq-cal-year">{yr}</span>
      </div>
      <div className="aq-cal-grid">
        {['Пн','Вт','Ср','Чт','Пт','Сб','Вс'].map(h => (
          <div key={h} className="aq-cal-head" style={{ color: accent }}>{h}</div>
        ))}
        {cells.map((c, i) => {
          const isWedding = c.cur && c.n === day;
          return (
            <div key={i}
              className={`aq-cal-cell${!c.cur ? ' aq-cal-muted' : ''}${isWedding ? ' aq-cal-today' : ''}`}
              style={isWedding ? { background: accent, color: '#fff' } : {}}
            >{c.n}</div>
          );
        })}
      </div>
      {addBtn}
    </div>
  );
}

/* ── RSVPForm ────────────────────────────────────────────────── */
function RSVPForm({ project, accent }) {
  const [status,  setStatus]  = useState('confirmed');
  const [name,    setName]    = useState('');
  const [survey,  setSurvey]  = useState({});
  const [message, setMessage] = useState('');
  const [done,    setDone]    = useState(false);
  const bc = project.block_content || {};

  if (done) return (
    <div className="aq-rsvp-done" style={{ borderColor: accent }}>
      <div className="aq-done-icon">✦</div>
      <strong style={{ color: accent }}>Спасибо!</strong>
      <p>{status === 'confirmed' ? 'Ждём вас с нетерпением!' : 'Жаль, что не сможете прийти.'}</p>
    </div>
  );

  return (
    <form className="aq-rsvp-form" onSubmit={e => { e.preventDefault(); setDone(true); }}>
      <div className="aq-rsvp-options">
        {[{ v: 'confirmed', l: bc.rsvp?.yesText || 'Приду!' }, { v: 'declined', l: bc.rsvp?.noText || 'Не смогу прийти' }].map(o => (
          <label key={o.v}
            className={`aq-rsvp-opt${status === o.v ? ' active' : ''}`}
            style={status === o.v ? { background: accent, color: '#fff', borderColor: accent } : {}}
          >
            <input type="radio" name="rsvp" value={o.v} checked={status === o.v}
              onChange={() => setStatus(o.v)} style={{ display: 'none' }} />
            {o.l}
          </label>
        ))}
      </div>

      <input className="aq-input" placeholder="Ваше имя"
        value={name} onChange={e => setName(e.target.value)} required />

      {project.blocks?.includes('survey') && status === 'confirmed' && project.survey?.map(q => (
        <div key={q.id} className="aq-survey-q">
          <p className="aq-survey-label">{q.question}</p>
          {q.type === 'text'
            ? <textarea className="aq-input" rows={2} value={survey[q.id] || ''}
                onChange={e => setSurvey(s => ({ ...s, [q.id]: e.target.value }))} />
            : q.type === 'multiple'
            ? <div className="aq-survey-opts">{(q.options || []).map(opt => {
                const selected = Array.isArray(survey[q.id]) ? survey[q.id].includes(opt) : false;
                return (
                  <label key={opt}
                    className={`aq-rsvp-opt aq-rsvp-opt-sm${selected ? ' active' : ''}`}
                    style={selected ? { background: accent, color: '#fff', borderColor: accent } : {}}
                  >
                    <input type="checkbox" checked={selected} onChange={e => {
                      const cur = Array.isArray(survey[q.id]) ? survey[q.id] : [];
                      setSurvey(s => ({ ...s, [q.id]: e.target.checked ? [...cur, opt] : cur.filter(v => v !== opt) }));
                    }} style={{ display: 'none' }} />
                    {opt}
                  </label>
                );
              })}</div>
            : <div className="aq-survey-opts">{(q.options || []).map(opt => (
                <label key={opt}
                  className={`aq-rsvp-opt aq-rsvp-opt-sm${survey[q.id] === opt ? ' active' : ''}`}
                  style={survey[q.id] === opt ? { background: accent, color: '#fff', borderColor: accent } : {}}
                >
                  <input type="radio" name={q.id} value={opt}
                    onChange={() => setSurvey(s => ({ ...s, [q.id]: opt }))} style={{ display: 'none' }} />
                  {opt}
                </label>
              ))}</div>
          }
        </div>
      ))}

      {project.blocks?.includes('message') && (
        <div>
          {bc.message?.fieldLabel && (
            <p style={{ fontSize: '0.82rem', color: '#9a9a9a', marginBottom: 4 }}>{bc.message.fieldLabel}</p>
          )}
          <textarea className="aq-input" rows={3}
            placeholder={bc.message?.text || 'Ваши пожелания молодым...'}
            value={message} onChange={e => setMessage(e.target.value)} />
        </div>
      )}
      <button type="submit" className="aq-btn" style={{ background: accent }}>
        {bc.rsvp?.btnText || 'Подтвердить'}
      </button>
    </form>
  );
}

/* ── Wishlist ────────────────────────────────────────────────── */
function Wishlist({ project, accent }) {
  const [reserved, setReserved] = useState({});
  const items = project.wishlist || [];
  if (!items.length) return null;
  return (
    <div className="aq-wishlist">
      {items.map(item => (
        <div key={item.id} className="aq-wish-item">
          <div className="aq-wish-info">
            <div className="aq-wish-name">{item.name}</div>
            {item.price && <div className="aq-wish-price" style={{ color: accent }}>{item.price} ₽</div>}
          </div>
          <div className="aq-wish-actions">
            {item.link && (
              <a href={item.link} target="_blank" rel="noopener noreferrer" className="aq-btn-outline">
                Посмотреть
              </a>
            )}
            {reserved[item.id]
              ? <button type="button" className="aq-btn" style={{ background: '#9a9a9a' }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: false }))}>
                  ✓ Снять резерв
                </button>
              : <button type="button" className="aq-btn" style={{ background: accent }}
                  onClick={() => setReserved(r => ({ ...r, [item.id]: true }))}>
                  Зарезервировать
                </button>
            }
          </div>
        </div>
      ))}
    </div>
  );
}

/* ── WatercolorDivider SVG ───────────────────────────────────── */
function WaveDivider({ color = '#f4a7b9', flip = false }) {
  return (
    <div className="aq-wave-divider" style={{ transform: flip ? 'scaleX(-1)' : 'none' }}>
      <svg viewBox="0 0 1200 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M0,40 C150,80 350,0 600,40 C850,80 1050,0 1200,40 L1200,80 L0,80 Z"
          fill={color} opacity="0.18"
        />
        <path
          d="M0,55 C200,20 400,70 700,45 C900,25 1100,65 1200,50 L1200,80 L0,80 Z"
          fill={color} opacity="0.12"
        />
      </svg>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   MAIN — Aquarelle
══════════════════════════════════════════════════════════════ */
export default function Aquarelle({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blocks  = project.blocks || [];
  useReveal(pageRef, blocks.join(','));

  const bc     = project.block_content || {};
  const accent = project.palette?.primary || '#e67e9a';

  const fontTitle = project.fonts?.title || 'Philosopher';
  const fontBody  = project.fonts?.body  || 'Nunito';
  const fontStyle = {
    '--f-title': `'${fontTitle}', serif`,
    '--f-body':  `'${fontBody}', sans-serif`,
    '--c-accent': accent,
  };

  /* Dynamic Google Fonts load */
  useEffect(() => {
    const id = 'aq-dynamic-fonts';
    let link = document.getElementById(id);
    if (!link) {
      link = document.createElement('link');
      link.id = id; link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const families = [fontTitle, fontBody].filter(Boolean);
    const query = families.map(f => `family=${encodeURIComponent(f)}:wght@300;400;600;700`).join('&');
    link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  }, [fontTitle, fontBody]);

  /* Photos */
  const photos    = project.cover_photos || (project.cover_photo ? [{ url: project.cover_photo }] : []);
  const invPhotos = project.invitation_photos || [];
  const invP1     = invPhotos[0] || photos[0] || null;
  const invP2     = invPhotos[1] || photos[1] || null;
  const dcPhotos  = project.dresscode_photos || [];

  const bride = project.bride || 'Невеста';
  const groom = project.groom || 'Жених';
  const venues = (project.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());

  const tomorrowISO = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); })();
  const weddingDate = project.wedding_date || tomorrowISO;

  const HAS_MENU      = blocks.includes('menu');
  const HAS_CALENDAR  = blocks.includes('calendar');
  const HAS_COUNTDOWN = blocks.includes('countdown');
  const HAS_SCHEDULE  = blocks.includes('schedule');
  const HAS_MAP = blocks.includes('map') && ((p?.venues || project?.venues || []).some(v => v?.address?.trim()) || !!(p?.wedding_address || project?.wedding_address));
  const HAS_DRESSCODE = blocks.includes('palette');
  const HAS_RSVP      = blocks.includes('rsvp');
  const HAS_WISHLIST  = blocks.includes('wishlist');

  const allItems  = project.schedule || [];
  const optional  = allItems.filter(i => i.optional);
  const mandatory = allItems.filter(i => !i.optional);
  const mainItems = optional.length > 0 ? mandatory : allItems;

  /* Google Calendar link */
  const calLink = (() => {
    const d = new Date(weddingDate);
    const pad = n => String(n).padStart(2, '0');
    const ymd = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
    const title = encodeURIComponent(`Свадьба ${bride} & ${groom}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${ymd}/${ymd}`;
  })();

  return (
    <div className="aq-page" ref={pageRef} style={fontStyle}
      data-template="Aquarelle" container-type="inline-size" container-name="aq-root">

      {/* ══ HERO ══════════════════════════════════════════════════ */}
      <section className="aq-hero">
        {/* Floating watercolor dots */}
        <div className="aq-dot aq-dot-1" />
        <div className="aq-dot aq-dot-2" />
        <div className="aq-dot aq-dot-3" />
        <div className="aq-dot aq-dot-4" />
        <div className="aq-dot aq-dot-5" />

        {/* Nav */}
        {HAS_MENU && (
          <nav className="aq-nav">
            <span className="aq-nav-brand">{bride} &amp; {groom}</span>
            <div className="aq-nav-links">
              {HAS_SCHEDULE  && <a href="#schedule">Тайминг</a>}
              {HAS_MAP       && <a href="#map">Локация</a>}
              {HAS_DRESSCODE && <a href="#dresscode">Дресс-код</a>}
              {HAS_RSVP      && <a href="#rsvp">Анкета</a>}
            </div>
          </nav>
        )}

        {/* Photo + blobs */}
        <div className="aq-hero-photo-wrap aq-reveal">
          {/* Watercolor blobs behind photo */}
          <div className="aq-blob aq-blob-1" />
          <div className="aq-blob aq-blob-2" />
          <div className="aq-blob aq-blob-3" />

          <div className="aq-hero-photo-frame">
            {photos[0] ? (
              <img src={src(photos[0].url)} alt={`${bride} & ${groom}`} style={{ filter: flt(photos[0]), objectPosition: objPos(photos[0]) }} />
            ) : (
              <div className="aq-hero-photo-empty">
                <span>Ваше фото</span>
              </div>
            )}
          </div>
        </div>

        {/* Text */}
        <div className="aq-hero-text aq-reveal-up">
          <p className="aq-hero-slogan">{bc.hero?.subtitle || project.wedding_slogan || 'Вы приглашены'}</p>
          <h1 className="aq-hero-names">
            <span className="aq-name">{bride}</span>
            <span className="aq-amp">&amp;</span>
            <span className="aq-name">{groom}</span>
          </h1>
          <p className="aq-hero-date">{fmtDate(weddingDate)}</p>
          {project.wedding_time && (
            <p className="aq-hero-time">{project.wedding_time}</p>
          )}
          {HAS_RSVP && (
            <a href="#rsvp" className="aq-btn aq-hero-btn" style={{ background: accent }}>
              {bc.rsvp?.yesText || 'Подтвердить присутствие'}
            </a>
          )}
        </div>
      </section>

      {/* ══ INVITATION TEXT ════════════════════════════════════════ */}
      <WaveDivider color={accent} />
      <section className="aq-section aq-invitation" id="invitation">
        <div className="aq-blob aq-blob-corner-tl" />
        <div className="aq-blob aq-blob-corner-br" />

        <div className="aq-invitation-inner">
          <div className="aq-reveal-left">
            <h2 className="aq-section-title aq-title-pink"
              dangerouslySetInnerHTML={{ __html: bc.message?.title || 'Дорогие гости!' }} />
            <div className="aq-body-text">
              {bc.message?.text
                ? <div dangerouslySetInnerHTML={{ __html: bc.message.text }} />
                : (
                  <>
                    <p>С радостью и трепетом в сердце приглашаем вас разделить с нами <strong>самый важный день</strong> нашей жизни.</p>
                    <p>В этот день мы соединим наши сердца в присутствии самых близких людей и создадим воспоминания, которые останутся навсегда.</p>
                  </>
                )
              }
            </div>
          </div>

          <div className="aq-invitation-photos aq-reveal">
            {invP1 && (
              <div className="aq-inv-photo aq-inv-photo-a">
                <img src={src(invP1.url)} alt="" style={{ filter: flt(invP1), objectPosition: objPos(invP1) }} />
              </div>
            )}
            {invP2 && (
              <div className="aq-inv-photo aq-inv-photo-b">
                <img src={src(invP2.url)} alt="" style={{ filter: flt(invP2), objectPosition: objPos(invP2) }} />
              </div>
            )}
            {!invP1 && (
              <div className="aq-inv-photo aq-inv-photo-empty">
                <span>Ваше фото</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ══ CALENDAR + COUNTDOWN ══════════════════════════════════ */}
      {(HAS_CALENDAR || HAS_COUNTDOWN) && (
        <section className="aq-section aq-cal-section" id="calendar">
          <WaveDivider color="#87ceeb" flip />
          <div className="aq-section-inner">
            {HAS_CALENDAR && (
              <div className="aq-bloom">
                <CalendarWidget
                  date={weddingDate}
                  accent={accent}
                  addBtn={
                    <a href={calLink} target="_blank" rel="noopener noreferrer"
                       className="aq-cal-add-btn" style={{ background: accent }}>
                      📅 {bc.calendar?.title || 'Добавить в календарь'}
                    </a>
                  }
                />
              </div>
            )}
            {HAS_COUNTDOWN && (
              <div className="aq-countdown-wrap aq-bloom" style={{ '--d': '.15s' }}>
                <h2 className="aq-section-title aq-title-blue"
                  dangerouslySetInnerHTML={{ __html: bc.countdown?.title || 'До свадьбы осталось' }} />
                <BlockSubtitle text={bc.countdown?.subtitle} className="aq-sub-text" />
                <Countdown date={weddingDate} accent={accent} />
              </div>
            )}
          </div>
        </section>
      )}

      {/* ══ SCHEDULE ══════════════════════════════════════════════ */}
      {HAS_SCHEDULE && (
        <>
          <WaveDivider color="#ffb347" />
          <section className="aq-section aq-schedule-section" id="schedule">
            <div className="aq-blob aq-blob-corner-tr" />
            <h2 className="aq-section-title aq-title-sage aq-reveal"
              dangerouslySetInnerHTML={{ __html: bc.schedule?.title || 'Тайминг' }} />
            <BlockSubtitle text={bc.schedule?.subtitle} className="aq-sub-text aq-reveal" />
            <div className="aq-timeline">
              {allItems.length > 0 ? (
                <>
                  {optional.length > 0 && (
                    <div className="aq-timeline-group">
                      <div className="aq-timeline-group-label">Необязательная часть</div>
                      {optional.map((item, i) => (
                        <div key={i} className="aq-timeline-item aq-reveal" style={{ '--d': `${i * 0.1}s` }}>
                          <div className="aq-timeline-dot" style={{ background: accent }} />
                          <div className="aq-timeline-time" style={{ color: accent }}>{item.time}</div>
                          <div className="aq-timeline-info">
                            <div className="aq-timeline-event">{item.event}</div>
                            {item.description && <div className="aq-timeline-desc">{item.description}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="aq-timeline-group">
                    {optional.length > 0 && <div className="aq-timeline-group-label">Обязательная часть</div>}
                    {mainItems.map((item, i) => (
                      <div key={i} className="aq-timeline-item aq-reveal" style={{ '--d': `${i * 0.1}s` }}>
                        <div className="aq-timeline-dot" style={{ background: accent }} />
                        <div className="aq-timeline-time" style={{ color: accent }}>{item.time}</div>
                        <div className="aq-timeline-info">
                          <div className="aq-timeline-event">{item.event}</div>
                          {item.description && <div className="aq-timeline-desc">{item.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                [['14:00','Сбор гостей'],['15:00','Торжественная церемония'],['16:30','Фотосессия'],['18:00','Банкет']].map(([t, e], i) => (
                  <div key={i} className="aq-timeline-item" style={{ opacity: 0.45 }}>
                    <div className="aq-timeline-dot" style={{ background: '#9a9a9a' }} />
                    <div className="aq-timeline-time" style={{ color: '#9a9a9a' }}>{t}</div>
                    <div className="aq-timeline-event">{e}</div>
                  </div>
                ))
              )}
            </div>
          </section>
        </>
      )}

      {/* ══ MAP ═══════════════════════════════════════════════════ */}
      {HAS_MAP && (
        <>
          <WaveDivider color={accent} flip />
          <section className="aq-section aq-map-section" id="map">
            <h2 className="aq-section-title aq-title-pink aq-reveal"
              dangerouslySetInnerHTML={{ __html: bc.map?.title || 'Место проведения' }} />
            <BlockSubtitle text={bc.map?.subtitle} className="aq-sub-text aq-reveal" />
            <div className="aq-venues">
              {venues.length > 0 ? venues.map((v, i) => (
                <div key={i} className="aq-venue-row aq-reveal" style={{ '--d': `${i * 0.1}s` }}>
                  <div className="aq-venue-pin">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill={accent}>
                      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                    </svg>
                  </div>
                  <div className="aq-venue-info">
                    {v.label && <span className="aq-venue-label" style={{ color: accent }}>{v.label} · </span>}
                    {v.name  && <span className="aq-venue-name">{v.name} · </span>}
                    <span>{v.address}</span>
                  </div>
                  <a href={`https://yandex.ru/maps/?text=${encodeURIComponent(v.address)}&z=16`}
                     target="_blank" rel="noopener noreferrer" className="aq-btn-outline">
                    Маршрут →
                  </a>
                </div>
              )) : (
                <div className="aq-venue-row" style={{ color: '#9a9a9a', fontSize: '0.9rem' }}>
                  {project.wedding_address || 'Адрес будет добавлен позже'}
                </div>
              )}
            </div>
            {(project.map_url || (project.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
            <div className="aq-map-frame aq-reveal" style={{ '--d': '.2s' }}>
              {(() => {
                if (project.map_url) {
                  return <MapFrame region={project.region} title="Карта" src={project.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const withCoords = venues.filter(v => v.lat && v.lon);
                if (withCoords.length > 0) {
                  const clat = withCoords.reduce((s, v) => s + v.lat, 0) / withCoords.length;
                  const clon = withCoords.reduce((s, v) => s + v.lon, 0) / withCoords.length;
                  const pts  = withCoords.map(v => `${v.lon},${v.lat},pm2rdm`).join('~');
                  const zoom = withCoords.length > 1 ? 12 : 16;
                  return <MapFrame region={project.region} title="Карта" src={`https://yandex.ru/map-widget/v1/?ll=${clon},${clat}&z=${zoom}&pt=${pts}&lang=ru_RU`} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                }
                const addr = venues.length > 0
                  ? venues.map(v => v.address).filter(Boolean).join(' — ')
                  : (project.wedding_address || '');
                if (!addr) {
                  return <MapFrame region={project.region} title="Карта" src="https://yandex.ru/map-widget/v1/?ll=37.617617,55.755811&z=12&lang=ru_RU" allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                }
                const mapSrc = venues.length > 1
                  ? `https://yandex.ru/map-widget/v1/?rtext=${venues.map(v => encodeURIComponent(v.address)).join('~')}&rtt=auto&lang=ru_RU`
                  : `https://yandex.ru/map-widget/v1/?text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                return <MapFrame region={project.region} title="Карта" src={mapSrc} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
              })()}
            </div>
            )}
          </section>
        </>
      )}

      {/* ══ DRESSCODE ═════════════════════════════════════════════ */}
      {HAS_DRESSCODE && (
        <>
          <WaveDivider color="#8fbc8f" />
          <section className="aq-section aq-dresscode-section" id="dresscode">
            <div className="aq-blob aq-blob-corner-bl" />
            <h2 className="aq-section-title aq-title-sage aq-reveal"
              dangerouslySetInnerHTML={{ __html: bc.palette?.title || 'Дресс-код' }} />
            <BlockSubtitle text={bc.palette?.subtitle} className="aq-body-text aq-reveal" tag="div" />
            <div className="aq-swatches aq-reveal">
              {(project.dresscode_palette || []).map((sw, i) => {
                const c = sw.color || '#fff';
                const hex = c.replace('#', '');
                const r = parseInt(hex.slice(0, 2), 16) || 255;
                const g = parseInt(hex.slice(2, 4), 16) || 255;
                const b = parseInt(hex.slice(4, 6), 16) || 255;
                const isLight = (r * 299 + g * 587 + b * 114) / 1000 > 160;
                return (
                  <div key={i} className="aq-swatch aq-bloom"
                    style={{ background: c, border: isLight ? '1px solid #e0e0e0' : 'none', '--d': `${i * 0.1}s` }}>
                    <span style={{ color: isLight ? '#2c3e50' : '#fff' }}>{sw.label}</span>
                  </div>
                );
              })}
            </div>
            {dcPhotos.length > 0 && (
              <div className="aq-dc-photos">
                {dcPhotos.map((p, i) => (
                  <div key={i} className="aq-dc-photo aq-bloom" style={{ '--d': `${i * 0.12}s` }}>
                    <img src={src(p.url)} alt="" style={{ filter: flt(p), objectPosition: objPos(p) }} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {/* ══ RSVP + WISHLIST ═══════════════════════════════════════ */}
      {(HAS_RSVP || HAS_WISHLIST) && (
        <>
          <WaveDivider color={accent} flip />
          <section className="aq-section aq-rsvp-section" id="rsvp">
            <div className="aq-blob aq-blob-corner-tl" />
            {HAS_RSVP && (
              <div className="aq-rsvp-block aq-bloom">
                <h2 className="aq-section-title aq-title-pink"
                  dangerouslySetInnerHTML={{ __html: bc.rsvp?.title || 'Подтверждение' }} />
                <BlockSubtitle text={bc.rsvp?.subtitle} className="aq-sub-text" />
                {bc.rsvp?.deadline && (
                  <p className="aq-deadline" style={{ color: accent }}>
                    Ответьте до: <strong>{new Date(bc.rsvp.deadline).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>
                  </p>
                )}
                <RSVPForm project={project} accent={accent} />
              </div>
            )}
            {HAS_WISHLIST && (
              <div className="aq-wishlist-block aq-bloom" style={{ '--d': '.15s' }}>
                <h2 className="aq-section-title aq-title-blue"
                  dangerouslySetInnerHTML={{ __html: bc.wishlist?.title || 'Вишлист' }} />
                <BlockSubtitle text={bc.wishlist?.subtitle} className="aq-sub-text" />
                <Wishlist project={project} accent={accent} />
              </div>
            )}
          </section>
        </>
      )}

      {/* ══ FOOTER ════════════════════════════════════════════════ */}
      <WaveDivider color="#f4a7b9" />
      <footer className="aq-footer">
        <div className="aq-footer-blob aq-blob-1" />
        <p className="aq-footer-names">{bride} &amp; {groom}</p>
        <p className="aq-footer-date">{fmtDate(weddingDate)}</p>
        <p className="aq-footer-heart">♥</p>
      </footer>

      {/* ══ WATERMARK (free plan) ═════════════════════════════════ */}
      {project.payment_plan === 'free' && (
        <div className="aq-watermark">
          Сделано на <strong>WeddingApp</strong>
        </div>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={isPreview} />
      )}
    </div>
  );
}
